'use client';

import type { Dayjs } from 'dayjs';
import type React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(quarterOfYear);
dayjs.extend(duration);
import { InputLabel } from 'src/components/Atoms/InputLabel';
import { FormControl } from 'src/components/Molecules/FormControl';
import { styled } from '@mui/material/styles';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import useTranslations from 'src/hooks/useTranslations';
type DateRangeValue = [Dayjs | null, Dayjs | null];
interface InputDateRangePickerProps {
  label: string;
  dateRange: DateRangeValue;
  name: string;
  relativeTo?: DateRangeValue;
  shortcuts?: boolean;
  defaultLabel?: string;
  onChange: (newValue: DateRangeValue) => void;
}
const DateRangeLabel = styled(InputLabel)(() => ({
  // top: theme.sizes.spacing['08'],
}));
const StyledDateRangePicker = styled(DateRangePicker)(({
  theme
}) => ({
  '&.MuiPickersTextField-root': {
    backgroundColor: theme.colors.surface.primary,
    '& .MuiPickersInputBase-sectionsContainer': {
      padding: `${theme.sizes.spacing['05']} ${theme.sizes.spacing.none}`
    },
    '& .MuiPickersInputBase-sectionContent ': {
      color: theme.colors.text.primary,
      fontSize: theme.fonts.size.sm,
      fontFamily: theme.fonts.family.body,
      fontStyle: 'normal',
      fontWeight: theme.fonts.weight.normal,
      lineHeight: theme.fonts.lineHeight['leading-5'],
      letterSpacing: theme.fonts.letterSpacing.wide,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  }
}));
const InputDateRangePicker: React.FC<InputDateRangePickerProps> = ({
  label,
  name,
  dateRange,
  relativeTo = undefined,
  shortcuts = true,
  defaultLabel = 'None',
  onChange = () => void 0
}) => {
  const [selectedRange, setSelectedRange] = useState<DateRangeValue>(dateRange);
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  const [storedRelativeRange, setStoredRelativeRange] = useState<DateRangeValue>(relativeTo ?? dateRange);
  const {
    t
  } = useTranslations();
  const handleChange = (newValue: DateRangeValue) => {
    if (!newValue || newValue.length !== 2 || newValue === selectedRange) {
      return;
    }
    setSelectedRange(newValue);
    onChange(newValue);
  };
  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = useMemo(() => [{
    label: t('components.input.dateRangeThisWeek'),
    getValue: () => {
      const today = dayjs.utc();
      return [today.startOf('week'), today.endOf('week')];
    }
  }, {
    label: t('components.input.dateRangeLastWeek'),
    getValue: () => {
      const today_0 = dayjs.utc();
      const prevWeek = today_0.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    }
  }, {
    label: t('components.input.dateRangeThisMonth'),
    getValue: () => {
      const today_1 = dayjs.utc();
      return [today_1.startOf('month'), today_1.endOf('month')];
    }
  }, {
    label: t('components.input.dateRangeLastMonth'),
    getValue: () => {
      const today_2 = dayjs.utc();
      const endOfLastMonth = today_2.startOf('month').subtract(1, 'day');
      return [endOfLastMonth.startOf('month'), endOfLastMonth];
    }
  }, {
    label: t('components.input.dateRangeThisQuarter'),
    getValue: () => {
      const today_3 = dayjs.utc();
      return [today_3.startOf('quarter'), today_3.endOf('quarter')];
    }
  }, {
    label: t('components.input.dateRangeLastQuarter'),
    getValue: () => {
      const today_4 = dayjs.utc();
      const endOfLastQuarter = today_4.startOf('quarter').subtract(1, 'day');
      return [endOfLastQuarter.startOf('quarter'), endOfLastQuarter];
    }
  }, {
    label: t('components.input.dateRangeThisYear'),
    getValue: () => {
      const today_5 = dayjs.utc();
      return [today_5.startOf('year'), today_5.endOf('year')];
    }
  }, {
    label: t('components.input.dateRangeLastYear'),
    getValue: () => {
      const today_6 = dayjs.utc();
      const endOfLastYear = today_6.startOf('year').subtract(1, 'day');
      return [endOfLastYear.startOf('year'), endOfLastYear];
    }
  }], [t]);
  const relativeItems: PickersShortcutsItem<DateRange<Dayjs>>[] = useMemo(() => [{
    label: t('components.input.dateRangePreviousPeriod'),
    getValue: () => {
      const [relativeStart, relativeEnd] = storedRelativeRange || [null, null];
      if (!relativeStart || !relativeEnd) {
        return selectedRange;
      }
      const duration = dayjs.duration(relativeEnd.diff(relativeStart));
      const daysDiff = Math.round(duration.asDays());
      let result: DateRangeValue = [null, null];
      if (daysDiff >= 365 && daysDiff <= 366) {
        result = [relativeStart.subtract(1, 'year'), relativeEnd.subtract(1, 'year')];
      } else if (daysDiff >= 90 && daysDiff <= 92) {
        result = [relativeStart.subtract(1, 'quarter'), relativeEnd.subtract(1, 'quarter')];
      } else if (daysDiff >= 28 && daysDiff <= 31) {
        result = [relativeStart.subtract(1, 'month'), relativeEnd.subtract(1, 'month')];
      } else if (duration.asWeeks() >= 1) {
        result = [relativeStart.subtract(1, 'week'), relativeEnd.subtract(1, 'week')];
      } else {
        result = [relativeStart.subtract(daysDiff, 'day'), relativeEnd.subtract(daysDiff, 'day')];
      }
      return result;
    }
  }, {
    label: t('components.input.dateRangePreviousYear'),
    getValue: () => storedRelativeRange ? [storedRelativeRange[0]?.subtract(1, 'year') ?? null, storedRelativeRange[1]?.subtract(1, 'year') ?? null] : selectedRange
  }, {
    label: t('components.input.dateRangeNone'),
    getValue: () => [null, null]
  }], [storedRelativeRange, selectedRange, t]);
  useEffect(() => {
    if (relativeTo && relativeTo[0] && relativeTo[1] && (relativeTo[0]?.format('YYYY-MM-DD') !== storedRelativeRange?.[0]?.format('YYYY-MM-DD') || relativeTo[1]?.format('YYYY-MM-DD') !== storedRelativeRange?.[1]?.format('YYYY-MM-DD'))) {
      // Deriving the comparison range means telling the parent, which cannot happen during render —
      // so this stays an effect. `storedRelativeRange` is the dedupe latch: once it equals
      // `relativeTo` the guard above is false, which is why depending on it converges instead of
      // looping.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStoredRelativeRange(relativeTo);
      setSelectedRange([relativeTo[0].subtract(1, 'year'), relativeTo[1].subtract(1, 'year')]);
      onChange([relativeTo[0].subtract(1, 'year'), relativeTo[1].subtract(1, 'year')]);
    }
  }, [relativeTo, storedRelativeRange, onChange]);
  return <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl variant='standard'>
        <DateRangeLabel shrink htmlFor={name}>
          {label}
        </DateRangeLabel>
        <StyledDateRangePicker showDaysOutsideCurrentMonth={false} disableHighlightToday format='YYYY-MM-DD' calendars={1} name={name} label={selectedRange[0] === null && selectedRange[1] === null && !pickerOpen ? defaultLabel : ''} timezone='UTC' value={selectedRange} onChange={handleChange} onOpen={() => setPickerOpen(true)} onClose={() => setPickerOpen(false)} slotProps={{
        shortcuts: {
          items: shortcuts ? relativeTo ? relativeItems : shortcutsItems : []
        }
      }} />
      </FormControl>
    </LocalizationProvider>;
};
export default InputDateRangePicker;
export type { InputDateRangePickerProps, DateRangeValue };